import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useNotifications } from "../../context/NotificationsContext";
import { approveLeave, rejectLeave, getLeaveHistory, getLeaveRequests } from "../../api/leave";
import { approveAdvance, rejectAdvance } from "../../api/advances";
import { approveOvertime, rejectOvertime, getOvertimeHistory } from "../../api/overtime";
import {
  approveWeeklyRest,
  rejectWeeklyRest,
  getWeeklyRestHistory,
} from "../../api/weeklyRest";
import { approvePermit, rejectPermit, getPermitHistory } from "../../api/permit";
import { approveTimesheet, rejectTimesheet } from "../../api/timesheet";
import {
  approveInventoryNight,
  rejectInventoryNight,
} from "../../api/inventoryNight";
import { filterVisiblePendingApprovals } from "../../utils/pendingActionBadges";
import { resolveAttachmentEntityType } from "../../api/attachments";
import AttachmentList from "../common/AttachmentList";
import { TeamLeaveRemainingPills } from "./TeamLeaveRemaining";
import {
  buildViewerSlaHold,
  getApprovalSlaProgress,
} from "../../utils/approvalSlaHold";
import { mapLeaveToPendingRequest, formatWorkplaceLabel } from "../../utils/dataMappers";
import EmployeePunchDaysModal from "../common/EmployeePunchDaysModal";
import {
  calendarMonthYearFromDate,
  toIsoDate,
} from "../../utils/fillMonthPunchDays";
import { getActiveAttendanceRegulation } from "../../api/attendanceRegulations";
import {
  compensatoryOvertimeRateHint,
  DEFAULT_OVERTIME_HOUR_RATES,
  paidOvertimeRateHint,
  ratesFromRegulation,
} from "../../utils/overtimeHourRates";
import "./PendingApprovals.css";

/** Hours before a delayed manager approval triggers a quarter-day penalty. */
const APPROVAL_SLA_HOURS = 24;
/** GM (المدير العام) delay-penalty window. */
const APPROVAL_SLA_HOURS_GM = 72;

function getCurrentApproverSlug(request) {
  const steps =
    request?.workflow?.steps || request?.request?.workflow?.steps || [];
  const current =
    request?.currentStep ?? request?.request?.currentStep ?? null;
  const step = steps.find(
    (s) => Number(s.stepOrder) === Number(current),
  );
  return step?.approverRole?.slug || null;
}

function getApprovalSlaHours(request) {
  const slug =
    getCurrentApproverSlug(request) ||
    request?.currentApproverRole ||
    request?.request?.currentApproverRole;
  if (slug === "gm" || request?.status === "gm_pending") {
    return APPROVAL_SLA_HOURS_GM;
  }
  return APPROVAL_SLA_HOURS;
}

function ApprovalSlaCountdown({ request, slaHold, now }) {
  const slaHours = useMemo(() => getApprovalSlaHours(request), [request]);
  const startMs = useMemo(() => {
    const raw =
      request?.stepEnteredAt ||
      request?.currentStepEnteredAt ||
      request?.request?.currentStepEnteredAt ||
      request?.createdAt;
    if (!raw) return null;
    const start = new Date(raw);
    if (Number.isNaN(start.getTime())) return null;
    return start.getTime();
  }, [request]);

  if (startMs == null) return null;

  const progress = getApprovalSlaProgress(
    new Date(startMs),
    slaHours,
    slaHold || {},
    new Date(now),
  );
  const remaining = progress.remainingMs;
  const overdue = remaining <= 0;
  const paused = !overdue && progress.paused;
  const urgent = !overdue && !paused && remaining <= 2 * 60 * 60 * 1000;

  return (
    <div
      className={`pending-sla-countdown${overdue ? " overdue" : ""}${
        paused ? " paused" : ""
      }${urgent ? " urgent" : ""}`}
    >
      <span className="pending-sla-label">
        {overdue
          ? "حان موعد الجزاء"
          : paused
            ? "متوقف أثناء الإجازة / الراحة الأسبوعية"
            : "متبقي على الجزاء"}
      </span>
      <span className="pending-sla-timer">
        {overdue
          ? `تجاوز ${slaHours} ساعة — يُطبق جزاء ربع يوم على المسؤول`
          : formatCountdownParts(remaining)}
      </span>
    </div>
  );
}

function formatCountdownParts(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return `${days}ي ${pad(remHours)}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

// ✅ السلفة الاستثنائية: مدير مباشر → HR (بدون مدير الإدارة)
function isExceptionalAdvanceRequest(request) {
  if (!request) return false;
  if (request._source === "advance" || request.type === "سلفة") {
    return Number(request.installments || 1) > 1;
  }
  return false;
}

function isOvertimeFinalSettlementStep(request) {
  if (!request || request._source !== "overtime") return false;
  if (request.source && request.source !== "EMPLOYEE") return false;
  if (request.settlement) return false;
  const steps = request.workflow?.steps || [];
  if (!steps.length) return false;
  const maxStep = Math.max(...steps.map((s) => Number(s.stepOrder) || 0));
  const current = Number(request.currentStep) || 0;
  return current >= maxStep;
}

const HISTORY_ACTION_LABELS = {
  CREATED: "تم تقديم الطلب",
  APPROVED: "وافق",
  REJECTED: "رفض",
  GM_OVERRIDE: "موافقة استثنائية",
  STEP_ADVANCED: "تقدّم للمرحلة التالية",
  STATUS_CHANGED: "تغيير الحالة",
  CANCELLED: "ألغى الطلب",
};

const HISTORY_ROLE_LABELS = {
  manager: "مدير مباشر",
  team_leader: "قائد فريق",
  supervisor: "مشرف",
  branch_manager: "مدير فرع",
  regional_manager: "مدير منطقة",
  department_manager: "مدير إدارة",
  hr: "موارد بشرية",
  hr_staff: "موظف موارد بشرية",
  gm: "المدير العام",
  admin: "أدمن",
};

function resolveHistoryPerformerName(item) {
  const performer = item.performedBy;
  if (!performer) {
    return item.roleName || item.role?.name || null;
  }
  return (
    performer.employee?.fullName ||
    performer.fullName ||
    performer.username ||
    performer.email ||
    item.roleName ||
    item.role?.name ||
    null
  );
}

function mapWorkflowHistory(history) {
  if (!history) return [];
  const actions = history.workflowActions;
  if (!Array.isArray(actions) || actions.length === 0) {
    const audits = history.auditLogs;
    if (!Array.isArray(audits)) return [];
    return audits
      .filter((a) => a.action !== "CREATED" && a.action !== "STEP_ADVANCED")
      .map((item, index) => ({
        id: item.id || `audit-${index}`,
        action: HISTORY_ACTION_LABELS[item.action] || item.action || "إجراء",
        role:
          item.roleName ||
          item.role?.name ||
          HISTORY_ROLE_LABELS[item.role?.slug] ||
          null,
        by: resolveHistoryPerformerName(item),
        notes: item.notes,
        at: item.createdAt,
        isReject: item.action === "REJECTED",
        isApprove:
          item.action === "APPROVED" || item.action === "GM_OVERRIDE",
      }));
  }

  return actions.map((item, index) => {
    const roleSlug = item.role?.slug;
    const roleName =
      item.roleName ||
      item.role?.name ||
      (roleSlug ? HISTORY_ROLE_LABELS[roleSlug] : null);
    return {
      id: item.id || `action-${index}`,
      action: HISTORY_ACTION_LABELS[item.action] || item.action || "إجراء",
      role: roleName,
      by: resolveHistoryPerformerName(item),
      notes: item.notes,
      at: item.createdAt,
      isReject: item.action === "REJECTED",
      isApprove:
        item.action === "APPROVED" || item.action === "GM_OVERRIDE",
    };
  });
}

function formatHistoryDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ar-EG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const HISTORY_SOURCES = new Set(["leave", "permit", "weekly-rest", "overtime"]);

function fetchRequestHistory(source, id) {
  if (source === "leave") return getLeaveHistory(id);
  if (source === "permit") return getPermitHistory(id);
  if (source === "weekly-rest") return getWeeklyRestHistory(id);
  if (source === "overtime") return getOvertimeHistory(id);
  return Promise.reject(new Error("unsupported history source"));
}

function RequestApprovalTrail({ source, entityId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId || !source) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    fetchRequestHistory(source, entityId)
      .then((history) => {
        if (!cancelled) setItems(mapWorkflowHistory(history));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [source, entityId]);

  if (loading) {
    return (
      <div className="pending-history">
        <div className="pending-history-title">سجل الموافقات</div>
        <p className="pending-history-empty">جاري تحميل السجل...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pending-history">
        <div className="pending-history-title">سجل الموافقات</div>
        <p className="pending-history-empty">
          لا توجد موافقات سابقة — أنت أول من يراجع هذا الطلب
        </p>
      </div>
    );
  }

  return (
    <div className="pending-history">
      <div className="pending-history-title">سجل الموافقات السابقة</div>
      <ul className="pending-history-list">
        {items.map((item) => (
          <li
            key={item.id}
            className={`pending-history-item${
              item.isReject
                ? " is-reject"
                : item.isApprove
                  ? " is-approve"
                  : ""
            }`}
          >
            <div className="pending-history-top">
              <strong>{item.action}</strong>
              <span>{formatHistoryDate(item.at)}</span>
            </div>
            {(item.by || item.role) && (
              <div className="pending-history-by">
                {item.by && item.role
                  ? `${item.by} — ${item.role}`
                  : item.by || item.role}
              </div>
            )}
            {item.notes ? (
              <div className="pending-history-notes">{item.notes}</div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PendingApprovals({ teamOnly = false, sources = null } = {}) {
  const { user } = useAuth();
  const {
    pendingRequests,
    leaveCalendarData,
    timesheetRestCalendarData,
    updatePendingRequests,
    employeesByBranch,
    teamMembers,
    refreshData,
  } = useData();
  const { addNotification, addNotificationToUser } = useNotifications();

  const [myApprovedLeaves, setMyApprovedLeaves] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionModal, setActionModal] = useState({
    open: false,
    type: "",
    notes: "",
    disposition: "",
  });
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [slaNow, setSlaNow] = useState(() => Date.now());
  const [modifyData, setModifyData] = useState({
    days: "",
    amount: "",
    hours: "",
    installments: "",
  });

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [punchModal, setPunchModal] = useState(null);
  const [overtimeRates, setOvertimeRates] = useState(
    DEFAULT_OVERTIME_HOUR_RATES,
  );

  useEffect(() => {
    const id = setInterval(() => setSlaNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => getActiveAttendanceRegulation?.())
      .then((regulation) => {
        if (!cancelled) setOvertimeRates(ratesFromRegulation(regulation));
      })
      .catch(() => {
        if (!cancelled) setOvertimeRates(DEFAULT_OVERTIME_HOUR_RATES);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;
    if (!userId) {
      setMyApprovedLeaves([]);
      return undefined;
    }

    getLeaveRequests({ limit: 100, page: 1 })
      .then((payload) => {
        const rows = Array.isArray(payload)
          ? payload
          : payload?.items || payload?.data || [];
        const mine = rows
          .map(mapLeaveToPendingRequest)
          .filter((leave) => {
            if (String(leave.userId) !== String(userId)) return false;
            return leave.status === "approved";
          })
          .map((leave) => ({
            startDate: leave.startDate,
            endDate: leave.endDate,
          }));
        if (!cancelled) setMyApprovedLeaves(mine);
      })
      .catch(() => {
        if (!cancelled) setMyApprovedLeaves([]);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const slaHold = useMemo(
    () => buildViewerSlaHold(user, myApprovedLeaves),
    [user, myApprovedLeaves],
  );

  const allowedSources = useMemo(
    () => (Array.isArray(sources) && sources.length > 0 ? new Set(sources) : null),
    [sources],
  );
  const isScopedApprovals = Boolean(allowedSources);
  const showLeaveFilter =
    !allowedSources || allowedSources.has("leave");
  const showWeeklyRestFilter =
    !allowedSources || allowedSources.has("weekly-rest");
  const showPermitFilter =
    !allowedSources || allowedSources.has("permit");
  const showAdvanceFilter =
    !allowedSources || allowedSources.has("advance");
  const showOvertimeFilter =
    !allowedSources || allowedSources.has("overtime");
  const showTimesheetFilter =
    !allowedSources || allowedSources.has("timesheet");
  const showInventoryNightFilter =
    !allowedSources || allowedSources.has("inventory_night");
  const showSourceFilters =
    [
      showLeaveFilter,
      showWeeklyRestFilter,
      showPermitFilter,
      showAdvanceFilter,
      showOvertimeFilter,
      showTimesheetFilter,
      showInventoryNightFilter,
    ].filter(Boolean).length > 1;

  const visibleRequests = useMemo(
    () =>
      filterVisiblePendingApprovals(pendingRequests, {
        user,
        teamMembers,
        teamOnly,
        sources,
      }),
    [pendingRequests, user, teamMembers, teamOnly, sources],
  );

  const filteredRequests =
    filter === "all"
      ? visibleRequests
      : visibleRequests.filter((r) => {
          const type = r.type || "";
          if (filter === "leave") return type.includes("إجازة");
          if (filter === "advance") return type === "سلفة";
          if (filter === "overtime") return type === "إضافي";
          if (filter === "permit")
            return type === "إذن" || type === "إذن تعويضي";
          if (filter === "weekly-rest") return type === "راحة أسبوعية";
          if (filter === "timesheet")
            return (
              type === "خطة أسبوعية" ||
              type === "خطة المخزن الأسبوعية" ||
              type === "خطة المخزن اليومية" ||
              type === "خطة الموديريتور الأسبوعية" ||
              r._source === "timesheet"
            );
          if (filter === "inventory_night") return type === "جرد" || r._source === "inventory_night";
          return true;
        });

  // ✅ FIX: بندور في الاتنين بشكل مستقل — مش بنشيل تكرار بالـ id
  // لأن نفس الـ id ممكن يكون عنده كود مختلف في كل مصدر
  function getEmployeeByCode(code) {
    // الأولوية لـ teamMembers عشان فيها الموظف الحالي بكوده الصح
    const fromTeam = (teamMembers || []).find((e) => e.code === code);
    if (fromTeam) return fromTeam;
    const fromBranches = Object.values(employeesByBranch || {}).flat();
    return fromBranches.find((e) => e.code === code);
  }

  function getEmployeeById(id) {
    if (!id) return null;
    const fromTeam = (teamMembers || []).find(
      (e) => String(e.id) === String(id),
    );
    if (fromTeam) return fromTeam;
    const fromBranches = Object.values(employeesByBranch || {}).flat();
    return fromBranches.find((e) => String(e.id) === String(id));
  }

  function getEmployeeByUserId(userId) {
    if (!userId) return null;
    const fromTeam = (teamMembers || []).find(
      (e) => String(e.userId) === String(userId),
    );
    if (fromTeam) return fromTeam;
    const fromBranches = Object.values(employeesByBranch || {}).flat();
    return fromBranches.find((e) => String(e.userId) === String(userId));
  }

  function resolveRequestEmployee(request) {
    if (!request) return null;
    return (
      (request.employeeId && getEmployeeById(request.employeeId)) ||
      (request.userId && getEmployeeByUserId(request.userId)) ||
      getEmployeeByCode(request.employeeCode)
    );
  }

  function getDisplayName(request) {
    const emp = resolveRequestEmployee(request);
    return emp?.name || request.employeeName || "موظف";
  }

  function getDisplayCode(request) {
    const emp = resolveRequestEmployee(request);
    return emp?.code || request.employeeCode || "";
  }

  function getWorkplace(request) {
    if (request?.workplaceLabel) return request.workplaceLabel;
    const emp = resolveRequestEmployee(request);
    return formatWorkplaceLabel(
      request?.branchName || emp?.branchName,
      request?.departmentName || emp?.departmentName,
    );
  }

  function openPunchDays(request) {
    const emp = resolveRequestEmployee(request);
    const employeeId = emp?.id || request?.employeeId;
    if (!employeeId) return;
    const dateValue =
      request.startDate || request.from || request.submittedAt || null;
    const { month, year } = calendarMonthYearFromDate(dateValue);
    setPunchModal({
      employeeId,
      employeeName: emp?.name || request.employeeName || "موظف",
      employeeCode: emp?.code || request.employeeCode || "",
      month,
      year,
      highlightDate: toIsoDate(request.startDate || request.from),
    });
  }

  // ✅ تحديد لو الطلب ده بيعدي على 2 مستويات بس (سلفة، إذن، راحة)
  function isTwoLevelRequest(request) {
    if (!request) return false;
    return isExceptionalAdvanceRequest(request);
  }

  function openActionModal(requestId, type) {
    const request = pendingRequests.find((r) => r.id === requestId);
    setSelectedRequest(requestId);
    setActionModal({
      open: true,
      type,
      notes: "",
      disposition:
        type === "approve" &&
        isOvertimeFinalSettlementStep(request) &&
        (request?.requestedSettlement === "PAID_OT" ||
          request?.requestedSettlement === "COMPENSATORY")
          ? request.requestedSettlement
          : "",
    });
    setError("");

    if (type === "modify" && request) {
      setModifyData({
        days: request.days || "",
        amount: request.amount || "",
        hours: request.hours || "",
        installments: request.installments || "",
      });
    }
  }

  function closeActionModal() {
    setSelectedRequest(null);
    setActionModal({ open: false, type: "", notes: "", disposition: "" });
    setError("");
    setSubmitting(false);
    setModifyData({ days: "", amount: "", hours: "", installments: "" });
  }

  function finishInboxAction(requestId) {
    updatePendingRequests((prev) =>
      prev.filter((row) => row.id !== requestId),
    );
    closeActionModal();
    refreshData({ silent: true, inboxOnly: true });
  }

  async function handleConfirmAction() {
    const { type, notes, disposition } = actionModal;
    const requestId = selectedRequest;

    if (type === "reject" && !notes.trim()) {
      setError("يجب إدخال سبب الرفض");
      return;
    }

    const request = pendingRequests.find((r) => r.id === requestId);
    if (
      type === "approve" &&
      isOvertimeFinalSettlementStep(request) &&
      !disposition
    ) {
      setError("اختر نوع التسوية: صرف مالي أو إذن تعويضي");
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    if (request?._source === "leave") {
      try {
        if (type === "approve") {
          await approveLeave(request._backendId || request.id, { notes });
        } else if (type === "reject") {
          await rejectLeave(request._backendId || request.id, { notes });
        } else {
          setSubmitting(false);
          setError("تعديل طلب الإجازة غير مدعوم حالياً");
          return;
        }

        finishInboxAction(requestId);
      } catch (err) {
        setSubmitting(false);
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر تنفيذ الإجراء على طلب الإجازة",
        );
      }
      return;
    }

    if (request?._source === "advance") {
      try {
        if (type === "approve") {
          await approveAdvance(request._backendId || request.id, { notes });
        } else if (type === "reject") {
          await rejectAdvance(request._backendId || request.id, { notes });
        } else {
          setSubmitting(false);
          setError("تعديل طلب السلفة غير مدعوم حالياً");
          return;
        }

        finishInboxAction(requestId);
      } catch (err) {
        setSubmitting(false);
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر تنفيذ الإجراء على طلب السلفة",
        );
      }
      return;
    }

    if (request?._source === "overtime") {
      try {
        if (type === "approve") {
          await approveOvertime(request._backendId || request.id, {
            notes,
            ...(disposition ? { disposition } : {}),
          });
        } else if (type === "reject") {
          await rejectOvertime(request._backendId || request.id, { notes });
        } else {
          setSubmitting(false);
          setError("تعديل طلب الإضافي غير مدعوم حالياً");
          return;
        }

        finishInboxAction(requestId);
      } catch (err) {
        setSubmitting(false);
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر تنفيذ الإجراء على طلب الإضافي",
        );
      }
      return;
    }

    if (request?._source === "weekly-rest") {
      try {
        if (type === "approve") {
          await approveWeeklyRest(request._backendId || request.id, { notes });
        } else if (type === "reject") {
          await rejectWeeklyRest(request._backendId || request.id, { notes });
        } else {
          setSubmitting(false);
          setError("تعديل طلب الراحة الأسبوعية غير مدعوم حالياً");
          return;
        }

        finishInboxAction(requestId);
      } catch (err) {
        setSubmitting(false);
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر تنفيذ الإجراء على طلب الراحة الأسبوعية",
        );
      }
      return;
    }

    if (request?._source === "timesheet") {
      try {
        if (type === "approve") {
          await approveTimesheet(request._backendId || request.id, { notes });
        } else if (type === "reject") {
          await rejectTimesheet(request._backendId || request.id, { notes });
        } else {
          setSubmitting(false);
          setError("تعديل الخطة الأسبوعية غير مدعوم حالياً");
          return;
        }

        finishInboxAction(requestId);
      } catch (err) {
        setSubmitting(false);
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر تنفيذ الإجراء على الخطة الأسبوعية",
        );
      }
      return;
    }

    if (request?._source === "inventory_night") {
      try {
        if (type === "approve") {
          await approveInventoryNight(request._backendId || request.id, {
            notes,
          });
        } else if (type === "reject") {
          await rejectInventoryNight(request._backendId || request.id, {
            notes,
          });
        } else {
          setSubmitting(false);
          setError("تعديل الجرد غير مدعوم من شاشة الموافقات");
          return;
        }

        finishInboxAction(requestId);
      } catch (err) {
        setSubmitting(false);
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر تنفيذ الإجراء على الجرد",
        );
      }
      return;
    }

    if (request?._source === "permit") {
      try {
        if (type === "approve") {
          await approvePermit(request._backendId || request.id, { notes });
        } else if (type === "reject") {
          await rejectPermit(request._backendId || request.id, { notes });
        } else {
          setSubmitting(false);
          setError("تعديل طلب الإذن غير مدعوم حالياً");
          return;
        }

        finishInboxAction(requestId);
      } catch (err) {
        setSubmitting(false);
        const message = err.response?.data?.message;
        setError(
          Array.isArray(message)
            ? message.join("\n")
            : message || "تعذر تنفيذ الإجراء على طلب الإذن",
        );
      }
      return;
    }

    if (type === "modify") {
      const request = pendingRequests.find((r) => r.id === requestId);
      let hasChanges = false;

      if (request.days !== undefined && modifyData.days !== "") {
        if (Number(modifyData.days) <= 0) {
          setSubmitting(false);
          setError("عدد الأيام يجب أن يكون أكبر من صفر");
          return;
        }
        hasChanges = true;
      }

      if (request.amount !== undefined && modifyData.amount !== "") {
        if (Number(modifyData.amount) <= 0) {
          setSubmitting(false);
          setError("قيمة السلفة يجب أن تكون أكبر من صفر");
          return;
        }
        hasChanges = true;
      }

      if (request.hours !== undefined && modifyData.hours !== "") {
        if (Number(modifyData.hours) <= 0) {
          setSubmitting(false);
          setError("عدد الساعات يجب أن يكون أكبر من صفر");
          return;
        }
        hasChanges = true;
      }

      if (!hasChanges && !notes.trim()) {
        setSubmitting(false);
        setError("يجب إجراء تعديل أو إضافة ملاحظات");
        return;
      }
    }

    const employee =
      resolveRequestEmployee(request) ||
      (request.employeeId && getEmployeeById(request.employeeId)) ||
      getEmployeeByCode(request.employeeCode);

    // ✅ تحديد الحالة الجديدة حسب نوع الطلب (طلبات محلية غير الإجازات)
    let newStatus;
    if (type === "approve") {
      if (isTwoLevelRequest(request)) {
        // سلفة استثنائية بعد موافقة المدير تروح لـ HR عبر الـ API
        newStatus = "approved";
      } else {
        // إجازات: موافقة مبدئية → مدير إدارة
        newStatus = "manager_approved";
      }
    } else if (type === "reject") {
      newStatus = "rejected";
    } else {
      newStatus = "modified";
    }

    updatePendingRequests((prev) =>
      prev.map((r) => {
        if (r.id !== requestId) return r;
        if (type === "modify") {
          return {
            ...r,
            days: modifyData.days !== "" ? Number(modifyData.days) : r.days,
            amount:
              modifyData.amount !== "" ? Number(modifyData.amount) : r.amount,
            hours: modifyData.hours !== "" ? Number(modifyData.hours) : r.hours,
            installments:
              modifyData.installments !== ""
                ? Number(modifyData.installments)
                : r.installments,
            status: "modified",
            managerNotes: notes,
            managerApprovedAt: new Date().toISOString(),
            managerApprovedBy: user?.id,
          };
        }
        return {
          ...r,
          status: newStatus,
          rejectionReason: type === "reject" ? notes : r.rejectionReason,
          managerNotes: notes,
          managerApprovedAt: new Date().toISOString(),
          managerApprovedBy: user?.id,
        };
      }),
    );

    // ====== إشعارات ======
    const NOTIFICATION_TYPES = {
      NEW_REQUEST: "new_request",
      APPROVED: "approval",
      REJECTED: "rejection",
      MODIFIED: "modified",
    };

    function createNotification({
      recipientId,
      recipientRole,
      type,
      title,
      message,
      relatedRequestId,
      senderName,
      senderRole,
      requestType,
      requestStatus,
    }) {
      return {
        id: Date.now().toString(36) + Math.random().toString(36).substr(2),
        recipientId,
        recipientRole,
        type,
        title,
        message,
        relatedRequestId,
        senderName,
        senderRole,
        requestType,
        requestStatus,
        read: false,
        createdAt: new Date().toISOString(),
      };
    }

    if (type === "approve") {
      if (isTwoLevelRequest(request)) {
        // ✅ سلفة / إذن / راحة: موافقة نهائية مباشرة → إشعار للموظف + HR
        const empNotif = createNotification({
          recipientId: request.employeeId || request.employeeCode,
          recipientRole: "employee",
          type: NOTIFICATION_TYPES.APPROVED,
          title: "✅ تمت الموافقة النهائية على طلبك",
          message: `تمت الموافقة النهائية على طلب ${request.type}`,
          relatedRequestId: requestId,
          senderName: user?.name || "المدير المباشر",
          senderRole: "manager",
          requestType: request.type,
          requestStatus: "approved",
        });
        addNotification(empNotif);
        addNotificationToUser(
          request.employeeId || request.employeeCode,
          empNotif,
        );

        // إشعار لـ HR
        const hrNotif = createNotification({
          recipientId: 100, // HR id
          recipientRole: "hr",
          type: NOTIFICATION_TYPES.NEW_REQUEST,
          title: "📋 طلب تمت الموافقة عليه",
          message: `المدير ${user?.name} وافق على طلب ${request.type} للموظف ${request.employeeName}`,
          relatedRequestId: requestId,
          senderName: user?.name || "المدير المباشر",
          senderRole: "manager",
          requestType: request.type,
          requestStatus: "approved",
        });
        addNotification(hrNotif);
        addNotificationToUser(100, hrNotif);
      } else {
        // ✅ إجازات: موافقة مبدئية → إشعار للموظف + مدير الإدارة
        const empNotif = createNotification({
          recipientId: request.employeeId || request.employeeCode,
          recipientRole: "employee",
          type: NOTIFICATION_TYPES.APPROVED,
          title: "✅ تمت الموافقة المبدئية على طلبك",
          message: `المدير المباشر وافق على طلب ${request.type} وتم إرساله لمدير الإدارة للموافقة النهائية`,
          relatedRequestId: requestId,
          senderName: user?.name || "المدير المباشر",
          senderRole: "manager",
          requestType: request.type,
          requestStatus: "manager_approved",
        });
        addNotification(empNotif);
        addNotificationToUser(
          request.employeeId || request.employeeCode,
          empNotif,
        );

        // إشعار لمدير الإدارة
        const deptManagerId = employee?.departmentManagerId;
        if (deptManagerId) {
          const dmNotif = createNotification({
            recipientId: deptManagerId,
            recipientRole: "department-manager",
            type: NOTIFICATION_TYPES.NEW_REQUEST,
            title: "📋 طلب إجازة جديد يحتاج موافقتك",
            message: `المدير ${user?.name} وافق على طلب ${request.type} للموظف ${request.employeeName} ويحتاج موافقتك النهائية`,
            relatedRequestId: requestId,
            senderName: user?.name || "المدير المباشر",
            senderRole: "manager",
            requestType: request.type,
            requestStatus: "manager_approved",
          });
          addNotification(dmNotif);
          addNotificationToUser(deptManagerId, dmNotif);
        }
      }
    } else if (type === "reject") {
      // ❌ رفض → إشعار للموظف (نفس الكود لكل الأنواع)
      const empNotif = createNotification({
        recipientId: request.employeeId || request.employeeCode,
        recipientRole: "employee",
        type: NOTIFICATION_TYPES.REJECTED,
        title: "❌ تم رفض طلبك",
        message: `تم رفض طلب ${request.type} من قبل المدير المباشر. السبب: ${notes}`,
        relatedRequestId: requestId,
        senderName: user?.name || "المدير المباشر",
        senderRole: "manager",
        requestType: request.type,
        requestStatus: "rejected",
      });
      addNotification(empNotif);
      addNotificationToUser(
        request.employeeId || request.employeeCode,
        empNotif,
      );
    } else if (type === "modify") {
      // ✏️ تعديل → إشعار للموظف (نفس الكود لكل الأنواع)
      const empNotif = createNotification({
        recipientId: request.employeeId || request.employeeCode,
        recipientRole: "employee",
        type: NOTIFICATION_TYPES.MODIFIED,
        title: "✏️ تم تعديل طلبك",
        message: `المدير المباشر عدل طلب ${request.type} ويحتاج موافقتك`,
        relatedRequestId: requestId,
        senderName: user?.name || "المدير المباشر",
        senderRole: "manager",
        requestType: request.type,
        requestStatus: "modified",
      });
      addNotification(empNotif);
      addNotificationToUser(
        request.employeeId || request.employeeCode,
        empNotif,
      );
    }

    closeActionModal();
  }

  function getRequestDetails(request) {
    if (request.type === "سلفة") {
      const kind =
        request.advanceKindLabel ||
        (Number(request.installments) > 1 ? "استثنائية" : "عادية");
      const parts = [
        `${request.amount} ج.م`,
        kind,
        `${request.installments || 1} أقساط`,
      ];
      if (Number(request.installments) > 1 && request.guarantorName) {
        parts.push(`ضامن: ${request.guarantorName}`);
      }
      return parts.join(" — ");
    }
    if (request.type === "إضافي" || request._source === "overtime") {
      const dates = `${request.hours} ساعة (${request.startDate || ""}${
        request.endDate && request.endDate !== request.startDate
          ? ` → ${request.endDate}`
          : ""
      })`;
      const pref =
        request.requestedSettlement === "PAID_OT"
          ? "رغبة الموظف: صرف مالي"
          : request.requestedSettlement === "COMPENSATORY"
            ? "رغبة الموظف: إذن تعويضي"
            : "";
      return pref ? `${dates} — ${pref}` : dates;
    }
    if (request.type === "إذن" || request.type === "إذن تعويضي" || request._source === "permit")
      return `${request.hours} ساعة (${request.permitStartTime || ""}–${request.permitEndTime || ""}) - ${request.startDate || request.permitDate || ""}${
        request.isCompensatory || request.type === "إذن تعويضي"
          ? " — تعويضي بدون خصم"
          : Number(request.deductedHours) > 0
            ? ` — خصم ${request.deductedHours} ساعة`
            : ""
      }`;
    if (request.type === "راحة أسبوعية")
      return `يوم ${request.weeklyRestDayLabel}`;
    if (
      request.type === "خطة أسبوعية" ||
      request.type === "خطة المخزن الأسبوعية" ||
      request.type === "خطة المخزن اليومية" ||
      request.type === "خطة الموديريتور الأسبوعية" ||
      request._source === "timesheet"
    ) {
      const empCount = (request.employees || []).length;
      const shiftSummary = (request.employees || [])
        .slice(0, 3)
        .map((emp) => {
          const shift =
            emp.shift === "EVENING"
              ? "مسائي"
              : emp.shift === "MORNING"
                ? "صباحي"
                : "";
          const hours =
            emp.scheduledStartTime && emp.scheduledEndTime
              ? `${emp.scheduledStartTime}–${emp.scheduledEndTime}`
              : "";
          return [emp.employeeName, shift, hours].filter(Boolean).join(" ");
        })
        .filter(Boolean)
        .join("، ");
      const amendmentLabel = request.isAmendment ? "تعديل بعد الاعتماد — " : "";
      const scopeLabel =
        request.scope === "CASHIER"
          ? "كاشيرية — "
          : request.scope === "DEPARTMENT"
            ? "تسويق — "
            : request.scope === "MODERATOR"
              ? "موديريتور — "
              : request.scope === "WAREHOUSE"
                ? "مخزن — "
                : "";
      return `${amendmentLabel}${scopeLabel}${request.branchName || "فرع"} — ${request.weekStartDate || ""} → ${request.weekEndDate || ""} (${request.entryCount || 0} يوم / ${empCount} موظف)${shiftSummary ? ` — ${shiftSummary}` : ""}`;
    }
    if (request.type === "جرد" || request._source === "inventory_night") {
      const otHours = (request.members || []).reduce(
        (s, m) => s + Number(m.overtimeHours || 0),
        0,
      );
      return `بداية ${request.plannedStart || ""} — ${(request.members || []).length} أعضاء${
        request.isNightPolicy ? " — ليلي" : ""
      }${otHours > 0 ? ` — زيادة ${Math.round(otHours * 100) / 100}س` : ""}`;
    }
    if (Number(request.days) === 0.5) {
      const half =
        request.halfDayPeriodLabel ||
        (request.halfDayPeriod === "FIRST_HALF"
          ? "أول الشيفت"
          : request.halfDayPeriod === "SECOND_HALF"
            ? "آخر الشيفت"
            : "");
      return `نص يوم${half ? ` (${half})` : ""} - ${request.startDate}`;
    }
    return `${request.days} أيام - من ${request.startDate}`;
  }

  function canModify(request) {
    if (
      request._source === "advance" ||
      request._source === "overtime" ||
      request._source === "leave" ||
      request._source === "weekly-rest" ||
      request._source === "permit" ||
      request._source === "timesheet" ||
      request._source === "inventory_night"
    ) {
      return false;
    }
    const type = request.type || "";
    return type.includes("إجازة") || type === "سلفة";
  }

  function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1).getDay();
  }

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const approvedCalendarEntries = useMemo(() => {
    const entries = [];
    const dayMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };

    pendingRequests
      .filter((r) => r.status === "approved")
      .forEach((req) => {
        if (req.type === "راحة أسبوعية" && req.weeklyRestDay) {
          const targetDay = dayMap[req.weeklyRestDay];
          const daysCount = new Date(year, month + 1, 0).getDate();
          for (let d = 1; d <= daysCount; d++) {
            if (new Date(year, month, d).getDay() === targetDay) {
              const dd = String(d).padStart(2, "0");
              const mm = String(month + 1).padStart(2, "0");
              entries.push({
                date: `${year}-${mm}-${dd}`,
                employee: req.employeeName,
                type: "راحة أسبوعية",
              });
            }
          }
        } else if (req.startDate) {
          const numDays = req.days || 1;
          const start = new Date(req.startDate);
          for (let i = 0; i < numDays; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            entries.push({
              date: d.toISOString().split("T")[0],
              employee: req.employeeName,
              type: req.type,
              hours: req.hours || null,
              days: req.days || null,
            });
          }
        }
      });

    return entries;
  }, [pendingRequests, year, month]);

  function getLeavesForDate(dateStr) {
    const staticLeaves = leaveCalendarData.filter((l) => l.date === dateStr);
    const timesheetRests = (timesheetRestCalendarData || []).filter(
      (l) => l.date === dateStr,
    );
    const dynamicLeaves = approvedCalendarEntries.filter(
      (l) => l.date === dateStr,
    );
    const seen = new Set();
    const merged = [];
    [...staticLeaves, ...timesheetRests, ...dynamicLeaves].forEach((l) => {
      const key = `${l.employee || ""}-${l.type || ""}-${l.date || ""}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(l);
    });
    return merged;
  }

  function formatDateStr(year, month, day) {
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  function getLeaveTypeColor(type) {
    const t = type || "";
    if (t.includes("إجازة")) return "leave-vacation";
    if (t === "إذن" || t === "إذن تعويضي") return "leave-permit";
    if (t === "راحة أسبوعية") return "leave-weekly";
    return "leave-other";
  }

  function navigateMonth(direction) {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setSelectedDay(null);
  }

  const monthName = currentDate.toLocaleDateString("ar-EG", {
    month: "long",
    year: "numeric",
  });
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const weekDays = [
    "الأحد",
    "الإثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];

  const pendingCount = visibleRequests.length;

  return (
    <div className="pending-approvals" dir="rtl">
      <div className="pending-header">
        <div>
          <h2 className="pending-title">
            {isScopedApprovals
              ? "موافقات الإجازات"
              : teamOnly
                ? "موافقات فريقي"
                : "طلبات بانتظار الموافقة"}
          </h2>
          <p className="pending-subtitle">
            {isScopedApprovals
              ? `إجازات وراحة أسبوعية وأذون تحتاج موافقتك — ${filteredRequests.length} طلب`
              : teamOnly
                ? `طلبات الموظفين التابعين لك مباشرة — ${filteredRequests.length} طلب بانتظار مراجعتك`
                : `لديك ${pendingCount} طلب${pendingCount !== 1 ? "ات" : ""} تحتاج لمراجعتك`}
          </p>
        </div>
      </div>

      {showSourceFilters && (
      <div className="pending-filters">
        <button
          className={`filter-btn ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          الكل
        </button>
        {showWeeklyRestFilter && (
        <button
          className={`filter-btn ${filter === "weekly-rest" ? "active" : ""}`}
          onClick={() => setFilter("weekly-rest")}
        >
          📅 راحة أسبوعية
        </button>
        )}
        {showTimesheetFilter && (
        <button
          className={`filter-btn ${filter === "timesheet" ? "active" : ""}`}
          onClick={() => setFilter("timesheet")}
        >
          خطة أسبوعية / مخزن
        </button>
        )}
        {showInventoryNightFilter && (
        <button
          className={`filter-btn ${filter === "inventory_night" ? "active" : ""}`}
          onClick={() => setFilter("inventory_night")}
        >
          جرد
        </button>
        )}
        {showLeaveFilter && (
        <button
          className={`filter-btn ${filter === "leave" ? "active" : ""}`}
          onClick={() => setFilter("leave")}
        >
          إجازات
        </button>
        )}
        {showAdvanceFilter && (
        <button
          className={`filter-btn ${filter === "advance" ? "active" : ""}`}
          onClick={() => setFilter("advance")}
        >
          سلف
        </button>
        )}
        {showOvertimeFilter && (
        <button
          className={`filter-btn ${filter === "overtime" ? "active" : ""}`}
          onClick={() => setFilter("overtime")}
        >
          إضافي
        </button>
        )}
        {showPermitFilter && (
        <button
          className={`filter-btn ${filter === "permit" ? "active" : ""}`}
          onClick={() => setFilter("permit")}
        >
          أذون
        </button>
        )}
      </div>
      )}

      <div className="pending-content">
        <div className="pending-list-wrapper">
          <div className="pending-list">
            {filteredRequests.length === 0 ? (
              <div className="pending-empty">
                <p>🎉 لا توجد طلبات معلقة حالياً</p>
              </div>
            ) : (
              filteredRequests.map((req) => (
                <div key={req.id} className="pending-card">
                  <div className="pending-card-header">
                    <div className="pending-employee">
                      <div className="pending-avatar">
                        {getDisplayName(req).charAt(0)}
                      </div>
                      <div>
                        {resolveRequestEmployee(req)?.id || req.employeeId ? (
                          <button
                            type="button"
                            className="pending-employee-name pending-employee-name-btn"
                            onClick={() => openPunchDays(req)}
                            title="عرض أيام البصمة"
                          >
                            {getDisplayName(req)}
                          </button>
                        ) : (
                          <div className="pending-employee-name">
                            {getDisplayName(req)}
                          </div>
                        )}
                        <div className="pending-employee-code">
                          {getDisplayCode(req)}
                        </div>
                        {getWorkplace(req) ? (
                          <div className="pending-employee-workplace">
                            {getWorkplace(req)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <span className="pending-type-badge">{req.type}</span>
                  </div>

                  <div className="pending-details">
                    <div className="pending-detail-row">
                      <span className="pending-detail-label">التفاصيل:</span>
                      <span className="pending-detail-value">
                        {getRequestDetails(req)}
                      </span>
                    </div>
                    {(req._source === "leave" ||
                      String(req.type || "").includes("إجازة")) && (
                      <div className="pending-detail-row pending-leave-balance-row">
                        <span className="pending-detail-label">
                          رصيد الإجازات:
                        </span>
                        <span className="pending-detail-value">
                          <TeamLeaveRemainingPills
                            employee={resolveRequestEmployee(req)}
                          />
                        </span>
                      </div>
                    )}
                    <div className="pending-detail-row">
                      <span className="pending-detail-label">السبب:</span>
                      <span className="pending-detail-value">
                        {req.reasonText || req.reason || "—"}
                      </span>
                    </div>
                    {req._source === "overtime" && req.requestedSettlement ? (
                      <div className="pending-detail-row">
                        <span className="pending-detail-label">
                          رغبة الموظف:
                        </span>
                        <span className="pending-detail-value">
                          {req.requestedSettlement === "COMPENSATORY"
                            ? "إذن تعويضي"
                            : "صرف مالي"}
                        </span>
                      </div>
                    ) : null}
                    {req.type === "سلفة" &&
                    Number(req.installments) > 1 &&
                    req.guarantorName ? (
                      <div className="pending-detail-row">
                        <span className="pending-detail-label">الضامن:</span>
                        <span className="pending-detail-value">
                          {req.guarantorName}
                        </span>
                      </div>
                    ) : null}
                    <div className="pending-detail-row">
                      <span className="pending-detail-label">
                        تاريخ التقديم:
                      </span>
                      <span className="pending-detail-value">
                        {req.submittedAt}
                      </span>
                    </div>
                    {req.type !== "سلفة" && req._source !== "advance" ? (
                      <ApprovalSlaCountdown
                        request={req}
                        slaHold={slaHold}
                        now={slaNow}
                      />
                    ) : null}
                    {req.files && (
                      <div className="pending-detail-row">
                        <span className="pending-detail-label">مستندات:</span>
                        <span className="pending-files">
                          {req.files.map((f, i) => (
                            <span key={i} className="pending-file-tag">
                              📎 {f}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                    {((req._backendId || req.id) &&
                      resolveAttachmentEntityType(req._source) !== "other") ||
                    HISTORY_SOURCES.has(req._source) ? (
                      <div className="pending-extras">
                        {(req._backendId || req.id) &&
                          resolveAttachmentEntityType(req._source) !==
                            "other" && (
                            <AttachmentList
                              entityType={resolveAttachmentEntityType(
                                req._source,
                              )}
                              entityId={req._backendId || req.id}
                              showDelete={false}
                              compact
                              hideIfEmpty
                            />
                          )}
                        {HISTORY_SOURCES.has(req._source) && (
                          <RequestApprovalTrail
                            source={req._source}
                            entityId={req._backendId || req.id}
                          />
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className="pending-actions">
                    <button
                      className="pending-btn reject"
                      onClick={() => openActionModal(req.id, "reject")}
                    >
                      رفض
                    </button>
                    {canModify(req) && (
                      <button
                        className="pending-btn modify"
                        onClick={() => openActionModal(req.id, "modify")}
                      >
                        تعديل
                      </button>
                    )}
                    <button
                      className="pending-btn approve"
                      onClick={() => openActionModal(req.id, "approve")}
                    >
                      موافقة
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="calendar-section">
          <div className="calendar-header">
            <button
              className="calendar-nav-btn"
              onClick={() => navigateMonth(-1)}
              type="button"
              aria-label="الشهر السابق"
            >
              ‹
            </button>
            <h3 className="calendar-title">{monthName}</h3>
            <button
              className="calendar-nav-btn"
              onClick={() => navigateMonth(1)}
              type="button"
              aria-label="الشهر التالي"
            >
              ›
            </button>
          </div>

          <div className="calendar-grid">
            {weekDays.map((day) => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
            {Array.from({ length: firstDay }, (_, i) => (
              <div key={`empty-${i}`} className="calendar-day empty" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dateStr = formatDateStr(year, month, day);
              const leaves = getLeavesForDate(dateStr);
              const isToday =
                new Date().toDateString() ===
                new Date(year, month, day).toDateString();
              const isSelected = selectedDay === dateStr;
              const hasWeeklyRest = leaves.some(
                (leave) => leave.type === "راحة أسبوعية",
              );
              const hasOtherLeave = leaves.some(
                (leave) => leave.type !== "راحة أسبوعية",
              );

              return (
                <div
                  key={day}
                  className={`calendar-day ${isToday ? "today" : ""} ${isSelected ? "selected" : ""} ${hasOtherLeave ? "has-leave" : ""} ${hasWeeklyRest ? "has-weekly-rest" : ""}`}
                  onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                >
                  <span className="day-number">{day}</span>
                  {leaves.length > 0 && (
                    <div className="day-leaves">
                      {leaves.slice(0, 2).map((leave, idx) => (
                        <span
                          key={idx}
                          className={`day-leave-dot ${getLeaveTypeColor(leave.type)}`}
                          title={`${leave.employee}: ${leave.type}`}
                        />
                      ))}
                      {leaves.length > 2 && (
                        <span className="day-leave-more">
                          +{leaves.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {selectedDay && (
            <div className="day-details">
              <h4 className="day-details-title">
                تفاصيل يوم{" "}
                {new Date(selectedDay).toLocaleDateString("ar-EG", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </h4>
              {(() => {
                const leaves = getLeavesForDate(selectedDay);
                if (leaves.length === 0)
                  return (
                    <p className="day-details-empty">
                      لا توجد إجازات في هذا اليوم
                    </p>
                  );
                return (
                  <div className="day-details-list">
                    {leaves.map((leave, idx) => (
                      <div
                        key={idx}
                        className={`day-detail-item ${getLeaveTypeColor(leave.type)}`}
                      >
                        <div className="detail-employee">{leave.employee}</div>
                        <div className="detail-type">{leave.type}</div>
                        {leave.hours && (
                          <div className="detail-hours">{leave.hours} ساعة</div>
                        )}
                        {leave.days != null && leave.days !== "" && (
                          <div className="detail-days">
                            {Number(leave.days) === 0.5
                              ? `نص يوم${
                                  leave.halfDayPeriodLabel
                                    ? ` · ${leave.halfDayPeriodLabel}`
                                    : ""
                                }`
                              : `${leave.days} يوم`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          <div className="calendar-legend">
            <div className="legend-item">
              <span className="legend-dot leave-vacation" />
              <span>إجازة</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot leave-permit" />
              <span>إذن</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot leave-weekly" />
              <span>راحة أسبوعية</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot today" />
              <span>اليوم</span>
            </div>
          </div>
        </div>
      </div>

      {actionModal.open && (
        <div className="pending-modal-overlay" onClick={closeActionModal}>
          <div className="pending-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="pending-modal-title">
              {actionModal.type === "approve"
                ? "✅ تأكيد الموافقة"
                : actionModal.type === "reject"
                  ? "❌ تأكيد الرفض"
                  : "✏️ تعديل الطلب"}
            </h3>
            <p className="pending-modal-subtitle">
              {actionModal.type === "approve"
                ? isTwoLevelRequest(
                    pendingRequests.find((r) => r.id === selectedRequest),
                  )
                  ? "هل أنت متأكد من الموافقة؟ سيتم إرسال السلفة الاستثنائية للموارد البشرية مباشرة (بدون مدير الإدارة)"
                  : "هل أنت متأكد من الموافقة على هذا الطلب؟ (سيتم إرساله لمدير الإدارة)"
                : actionModal.type === "reject"
                  ? "هل أنت متأكد من رفض هذا الطلب؟"
                  : "قم بتعديل بيانات الطلب حسب الحاجة"}
            </p>

            {(() => {
              const modalRequest = pendingRequests.find(
                (r) => r.id === selectedRequest,
              );
              if (!HISTORY_SOURCES.has(modalRequest?._source)) return null;
              return (
                <RequestApprovalTrail
                  source={modalRequest._source}
                  entityId={modalRequest._backendId || modalRequest.id}
                />
              );
            })()}

            {actionModal.type === "modify" && (
              <div className="pending-modify-fields">
                {(() => {
                  const request = pendingRequests.find(
                    (r) => r.id === selectedRequest,
                  );
                  if (!request) return null;
                  return (
                    <>
                      {request.days !== undefined && (
                        <div className="pending-modal-field">
                          <label className="pending-modal-label">
                            عدد الأيام (الحالي: {request.days})
                          </label>
                          <input
                            type="number"
                            min="1"
                            className="pending-modal-input"
                            value={modifyData.days}
                            onChange={(e) =>
                              setModifyData((prev) => ({
                                ...prev,
                                days: e.target.value,
                              }))
                            }
                            placeholder="عدد الأيام الجديد"
                          />
                        </div>
                      )}
                      {request.amount !== undefined && (
                        <>
                          <div className="pending-modal-field">
                            <label className="pending-modal-label">
                              قيمة السلفة (الحالية: {request.amount} ج.م)
                            </label>
                            <input
                              type="number"
                              min="1"
                              className="pending-modal-input"
                              value={modifyData.amount}
                              onChange={(e) =>
                                setModifyData((prev) => ({
                                  ...prev,
                                  amount: e.target.value,
                                }))
                              }
                              placeholder="القيمة الجديدة"
                            />
                          </div>
                          <div className="pending-modal-field">
                            <label className="pending-modal-label">
                              عدد الأقساط (الحالي: {request.installments})
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="6"
                              className="pending-modal-input"
                              value={modifyData.installments}
                              onChange={(e) =>
                                setModifyData((prev) => ({
                                  ...prev,
                                  installments: e.target.value,
                                }))
                              }
                              placeholder="عدد الأقساط الجديد"
                            />
                          </div>
                        </>
                      )}
                      {request.hours !== undefined && (
                        <div className="pending-modal-field">
                          <label className="pending-modal-label">
                            عدد الساعات (الحالي: {request.hours})
                          </label>
                          <input
                            type="number"
                            min="0.5"
                            max="4"
                            step="0.5"
                            className="pending-modal-input"
                            value={modifyData.hours}
                            onChange={(e) =>
                              setModifyData((prev) => ({
                                ...prev,
                                hours: e.target.value,
                              }))
                            }
                            placeholder="عدد الساعات الجديد"
                          />
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {actionModal.type === "approve" &&
              isOvertimeFinalSettlementStep(
                pendingRequests.find((r) => r.id === selectedRequest),
              ) && (
                <div className="pending-modal-field">
                  <label className="pending-modal-label">
                    نوع التسوية * (القرار النهائي للموارد البشرية)
                  </label>
                  {pendingRequests.find((r) => r.id === selectedRequest)
                    ?.requestedSettlement ? (
                    <p className="pending-ot-preference-hint">
                      رغبة الموظف:{" "}
                      {pendingRequests.find((r) => r.id === selectedRequest)
                        ?.requestedSettlement === "COMPENSATORY"
                        ? "إذن تعويضي"
                        : "صرف مالي"}
                    </p>
                  ) : null}
                  <div className="pending-ot-disposition">
                    <label className="pending-ot-disposition-option">
                      <input
                        type="radio"
                        name="ot-disposition"
                        value="PAID_OT"
                        checked={actionModal.disposition === "PAID_OT"}
                        onChange={() =>
                          setActionModal((prev) => ({
                            ...prev,
                            disposition: "PAID_OT",
                          }))
                        }
                      />
                      صرف مالي (أوفر تايم في المرتب)
                      <small className="d-block text-muted">
                        {paidOvertimeRateHint(
                          overtimeRates.overtimePaidHourMultiplier,
                        )}
                      </small>
                    </label>
                    <label className="pending-ot-disposition-option">
                      <input
                        type="radio"
                        name="ot-disposition"
                        value="COMPENSATORY"
                        checked={actionModal.disposition === "COMPENSATORY"}
                        onChange={() =>
                          setActionModal((prev) => ({
                            ...prev,
                            disposition: "COMPENSATORY",
                          }))
                        }
                      />
                      إذن تعويضي (رصيد ساعات)
                      <small className="d-block text-muted">
                        {compensatoryOvertimeRateHint(
                          overtimeRates.overtimeCompensatoryHourMultiplier,
                        )}
                      </small>
                    </label>
                  </div>
                </div>
              )}

            <div className="pending-modal-field">
              <label className="pending-modal-label">
                {actionModal.type === "approve"
                  ? "ملاحظات (اختياري)"
                  : actionModal.type === "reject"
                    ? "سبب الرفض *"
                    : "ملاحظات على التعديل (اختياري)"}
              </label>
              <textarea
                className={`pending-modal-textarea ${error ? "has-error" : ""}`}
                rows={3}
                placeholder={
                  actionModal.type === "approve"
                    ? "ملاحظات على الموافقة..."
                    : actionModal.type === "reject"
                      ? "اكتب سبب الرفض هنا..."
                      : "ملاحظات على التعديل..."
                }
                value={actionModal.notes}
                onChange={(e) => {
                  setActionModal((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }));
                  if (error) setError("");
                }}
              />
              {error && <p className="pending-modal-error">{error}</p>}
            </div>

            <div className="pending-modal-buttons">
              <button
                className="pending-modal-btn secondary"
                onClick={closeActionModal}
                disabled={submitting}
              >
                إلغاء
              </button>
              <button
                className={`pending-modal-btn ${actionModal.type === "approve" ? "approve" : actionModal.type === "reject" ? "reject" : "modify"}`}
                onClick={handleConfirmAction}
                disabled={submitting}
              >
                {submitting
                  ? "جاري التنفيذ..."
                  : actionModal.type === "approve"
                  ? isTwoLevelRequest(
                      pendingRequests.find((r) => r.id === selectedRequest),
                    )
                    ? "موافقة وإرسال للموارد البشرية"
                    : "تأكيد الموافقة"
                  : actionModal.type === "reject"
                    ? "تأكيد الرفض"
                    : "حفظ التعديل"}
              </button>
            </div>
          </div>
        </div>
      )}

      {punchModal ? (
        <EmployeePunchDaysModal
          employeeId={punchModal.employeeId}
          employeeName={punchModal.employeeName}
          employeeCode={punchModal.employeeCode}
          month={punchModal.month}
          year={punchModal.year}
          highlightDate={punchModal.highlightDate}
          onClose={() => setPunchModal(null)}
        />
      ) : null}
    </div>
  );
}

export default PendingApprovals;
